import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { downloadPdf } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Award, Download, QrCode, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

export default function CertificatesPage() {
  const [certs, setCerts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    api.get("/certificates").then((r) => setCerts(r.data));
  }, []);

  const openCert = async (c) => {
    setSelected(c);
    const { data } = await api.get(`/certificates/${c.id}/qr`);
    setQrData(data);
  };

  const handleDownload = async (c) => {
    setDownloading(c.id);
    try {
      await downloadPdf(`/certificates/${c.id}/pdf`);
      toast.success("Certificate PDF downloaded");
    } catch (e) {
      toast.error("Could not download certificate PDF.");
    }
    setDownloading(null);
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
          Recognition
        </p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
          Certificates
        </h1>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {certs.map((c) => (
            <div key={c.id} className="rounded-xl bg-white border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all" data-testid={`cert-card-${c.code}`}>
              <div className="bg-[#0A192F] p-6 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(#D4AF37 1px, transparent 1px)", backgroundSize: "16px 16px" }} />
                <div className="relative text-white">
                  <Award className="h-6 w-6 text-[#D4AF37]" />
                  <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                    Certificate of
                  </p>
                  <p className="font-serif text-2xl mt-1">{c.award_type}</p>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-semibold tracking-tight text-[#0A192F]">{c.delegate_name}</h3>
                <p className="text-sm text-slate-500 mt-1">{c.event_title} · {c.council}</p>
                <p className="mt-3 font-mono text-xs text-slate-500">{c.code}</p>
                <div className="mt-5 flex gap-2">
                  <Button onClick={() => openCert(c)} className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid={`view-cert-${c.code}`}>
                    <QrCode className="h-4 w-4 mr-1" /> View
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleDownload(c)}
                    disabled={downloading === c.id}
                    className="border-[#0F4C81] text-[#0F4C81]"
                    data-testid={`download-cert-${c.code}`}
                  >
                    {downloading === c.id ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-1" />
                    )}
                    PDF
                  </Button>
                </div>
              </div>
            </div>
          ))}
          {certs.length === 0 && (
            <div className="col-span-full rounded-xl bg-white border-2 border-dashed border-slate-200 p-10 text-center">
              <Award className="h-5 w-5 text-slate-400 mx-auto" />
              <p className="mt-2 text-sm text-slate-500">No certificates yet. Attend an event to earn one.</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Certificate preview</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="bg-white rounded-lg relative">
              <div className="border-4 border-[#D4AF37] p-10 text-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "radial-gradient(#0A192F 1px, transparent 1px)", backgroundSize: "12px 12px" }} />
                <div className="relative">
                  <p className="text-[10px] uppercase tracking-[0.4em] text-[#0A192F] font-bold">
                    {selected.organization_name}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.4em] text-slate-500 mt-1">
                    Presents
                  </p>
                  <h2 className="font-serif text-4xl text-[#0A192F] mt-4">Certificate</h2>
                  <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-bold mt-1">
                    of {selected.award_type}
                  </p>
                  <p className="mt-10 text-sm text-slate-500">This is to certify that</p>
                  <h3 className="mt-2 font-serif text-3xl text-[#0A192F]">{selected.delegate_name}</h3>
                  <p className="mt-6 max-w-lg mx-auto text-sm text-slate-600 leading-relaxed">
                    representing <span className="font-semibold">{selected.country}</span> in the{" "}
                    <span className="font-semibold">{selected.council}</span>, has been awarded at the{" "}
                    <span className="font-semibold">{selected.event_title}</span>.
                  </p>
                  <div className="mt-10 flex items-end justify-between">
                    <div className="text-left">
                      <p className="font-mono text-[10px] text-slate-500">{selected.code}</p>
                      <p className="text-xs text-slate-500">Issued {new Date(selected.issued_at).toLocaleDateString()}</p>
                    </div>
                    {qrData?.qr_data_url && (
                      <div className="flex flex-col items-center">
                        <img src={qrData.qr_data_url} alt="QR" className="h-20 w-20" data-testid="cert-qr-image" />
                        <p className="text-[8px] uppercase tracking-[0.22em] mt-1 text-slate-500">
                          Verify: {qrData.verify_url?.replace(/^https?:\/\//, "").slice(0, 28)}…
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
